<template>
  <b-overlay :show="formShow" rounded="sm" no-fade class="mt-1">
    <b-card>
      <b-row class="mb-1">
        <b-col
          cols="12"
          md="7"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <b-button
            variant="outline-primary"
            class="btn-icon"
            @click="$router.go(-1)"
          >
            <i class="fa-solid fa-arrow-left"></i>
            <span class="ml-25">Back</span>
          </b-button>
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <table class="table table-sm">
          <thead>
            <tr>
              <th scope="col">
                {{ priorityId ? "Priority Edit" : "Priority Add" }}
              </th>
            </tr>
          </thead>
        </table>
      </b-row>

      <validation-observer ref="priorityRules">
        <b-form>
          <b-row>
            <b-col cols="6" md="3" sm="12">
              <b-form-group label="Year" label-for="year">
                <validation-provider
                  name="year"
                  #default="{ errors }"
                  rules="required"
                >
                  <v-select
                    v-model="priorityValue.year"
                    :options="years"
                    :clearable="false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="6" md="12" sm="12">
              <b-form-group label="Priority Objective" label-for="objective">
                <validation-provider
                  name="objective"
                  #default="{ errors }"
                  rules="required"
                >
                  <v-select
                    v-model="priorityValue.objective_id"
                    label="title"
                    :reduce="(val) => val.id"
                    :options="objectives"
                    :clearable="true"
                  ></v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <hr />

          <b-row>
            <b-col cols="12">
              <b-button
                type="submit"
                @click.prevent="formSubmitted"
                variant="primary"
                class="float-right"
              >
                {{ priorityId ? "Update Priority" : "Create Priority" }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </b-overlay>
</template>

<script>
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import vSelect from "vue-select";
import router from "@/router";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";

export default {
  components: {
    vSelect,
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },

  setup() {
    const PRIORITY_CODE_APP_STORE_MODULE_NAME = "settings";

    if (!store.hasModule(PRIORITY_CODE_APP_STORE_MODULE_NAME)) {
      store.registerModule(
        PRIORITY_CODE_APP_STORE_MODULE_NAME,
        settingsStoreModule
      );
    }

    onUnmounted(() => {
      if (store.hasModule(PRIORITY_CODE_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(PRIORITY_CODE_APP_STORE_MODULE_NAME);
      }
    });
  },

  data() {
    return {
      priorityValue: {
        objective_id: "",
        year: null,
      },
      years: Array.from({ length: 10 }, (_, i) => 2020 + i),
      objectives: [],
      formShow: false,
      priorityId: null,
    };
  },

  mounted() {
    this.initializeForm();
  },

  methods: {
    async initializeForm() {
      try {
        await this.getObjectives();

        if (router.currentRoute.params.id) {
          this.priorityId = parseInt(router.currentRoute.params.id);
          await this.getPriorityById();
        }
      } catch (error) {
        this.handleError("Failed to initialize form.");
      }
    },

    getObjectives() {
      return store.dispatch("settings/getAllObjectives").then((res) => {
        this.objectives = res.data;
      });
    },

    getPriorityById() {
      return store
        .dispatch("settings/getPriorityById", { id: this.priorityId })
        .then((res) => {
          this.priorityValue = res.data;
        });
    },

    formSubmitted() {
      this.formShow = true;

      this.$refs.priorityRules.validate().then((success) => {
        if (success) {
          const priority = this.priorityId
            ? "settings/updatePriority"
            : "settings/savePriority";

          store
            .dispatch(priority, this.priorityValue)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Successful",
                  text: this.priorityId
                    ? "✔️ Priority Update Successfully"
                    : "✔️ Priority Added Successfully",
                  icon: "ThumbsUpIcon",
                  variant: "success",
                },
              });

              router.push({ name: "settings-priorities" });
            })
            .catch(() => {
              this.handleError("An error occurred while saving the priority.");
            })
            .finally(() => {
              this.formShow = false;
            });
        } else {
          this.formShow = false;
        }
      });
    },

    handleError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          text: message,
          icon: "AlertCircleIcon",
          variant: "danger",
        },
      });
      this.formShow = false;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
